import { useProtecaoFormulario } from "@/shared/hooks/useProtecaoSaida";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useIgreja } from "@/core/hooks/useIgreja";
import { useVoltarOu } from "@/modules/visitantes/hooks/useVoltarOu";
import { FluxoCadastro } from "@/modules/visitantes/components/FluxoCadastro";
import { SemPermissao } from "@/modules/visitantes/components/PaginaFormulario";
import {
  useEditarInformado,
  useMinhasIndicacoes,
  useMinisteriosQueLidero,
  usePodeInformar,
} from "@/modules/visitantes/hooks/useVisitantes";
import type {
  DadosFamiliar,
  DadosVisitante,
} from "@/modules/visitantes/services/visitantesService";
import { familiaresParaLinhas, indicacaoParaDados } from "@/modules/visitantes/utils/conversao";

/** O autor corrige o Visitante pendente enquanto o ministério responsável ainda não avaliou. */
export function EditarInformadoPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const voltar = useVoltarOu("/visitantes?aba=meus");
  const protecao = useProtecaoFormulario();
  const { podeInformar, carregando } = usePodeInformar();
  const { data: meus, isLoading } = useMinhasIndicacoes(podeInformar);
  const { data: ministerios = [] } = useMinisteriosQueLidero(podeInformar);
  const { data: igreja } = useIgreja();
  const editar = useEditarInformado();
  const [erro, setErro] = useState<string | null>(null);

  if (carregando || (podeInformar && isLoading))
    return <p className="px-4 py-10 text-center text-sm text-muted">Carregando…</p>;
  if (!podeInformar)
    return <SemPermissao texto="Só quem cadastrou o visitante pendente pode editar." />;
  const pendente = meus?.find((i) => i.id === id);
  if (!pendente || pendente.situacao !== "pendente") {
    return (
      <SemPermissao texto="Visitante pendente não encontrado ou já avaliado pelo ministério responsável." />
    );
  }

  const contato = igreja
    ? { email: igreja.contato_email, telefone: igreja.contato_telefone }
    : null;

  async function enviar(dados: DadosVisitante, familiares: DadosFamiliar[]) {
    if (!pendente) return;
    setErro(null);
    try {
      await editar.mutateAsync({ id: pendente.id, versao: pendente.versao, dados, familiares });
      protecao.liberar();
      navigate("/visitantes?aba=meus", {
        replace: true,
        state: { aviso: "Visitante pendente atualizado." },
      });
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível salvar as alterações.");
    }
  }

  return (
    <FluxoCadastro
      modo="informe"
      protecao={protecao}
      titulo="Editar visitante pendente"
      descricao="Você pode corrigir enquanto o ministério responsável não avaliou."
      ministerios={ministerios}
      contatoIgreja={contato}
      inicial={indicacaoParaDados(pendente)}
      linhasIniciais={familiaresParaLinhas(pendente)}
      // O consentimento é registrado na criação; a edição não o altera (nem pergunta de novo).
      consentimentoRegistrado
      enviando={editar.isPending}
      erro={erro}
      onLimparErro={() => setErro(null)}
      onEnviarSimples={(dados) => enviar(dados, [])}
      onEnviarFamilia={(principal, membros) => enviar(principal, membros)}
      rotuloEnviarFamilia={() => "Salvar"}
      onCancelar={voltar}
    />
  );
}
